/**
 * Draws a radial-bar mic ring from live analyser data.
 * Tracks an adaptive noise floor so room hush collapses to tiny ticks, while
 * speech still jumps hard toward the outer edge.
 */

let adaptiveNoiseFloor = 0.04
let displayMaster = 0

export function resetCircularAudioVisualizerEnvelope(): void {
  adaptiveNoiseFloor = 0.04
  displayMaster = 0
}

export function paintCircularAudioVisualizer(
  canvas: HTMLCanvasElement,
  frequencyData: Uint8Array,
  options?: { paused?: boolean; timeDomainData?: Uint8Array }
): void {
  const ctx = canvas.getContext("2d")
  if (!ctx) {
    return
  }

  const dpr = window.devicePixelRatio || 1
  const cssWidth =
    canvas.clientWidth || Number(canvas.getAttribute("width")) || 72
  const cssHeight =
    canvas.clientHeight || Number(canvas.getAttribute("height")) || 72
  const pixelW = Math.max(1, Math.round(cssWidth * dpr))
  const pixelH = Math.max(1, Math.round(cssHeight * dpr))
  if (canvas.width !== pixelW || canvas.height !== pixelH) {
    canvas.width = pixelW
    canvas.height = pixelH
  }

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, cssWidth, cssHeight)

  const cx = cssWidth / 2
  const cy = cssHeight / 2
  const barCount = 40
  const half = Math.min(cssWidth, cssHeight) / 2
  const pad = Math.max(2.5, half * 0.06)
  const maxOuter = Math.max(8, half - pad)
  const innerRadius = maxOuter * 0.48
  const maxBar = Math.max(4, maxOuter - innerRadius)
  const paused = Boolean(options?.paused)

  let peak = 0
  let rms = 0
  const timeData = options?.timeDomainData
  if (timeData && timeData.length > 0) {
    let sumSq = 0
    for (let i = 0; i < timeData.length; i += 1) {
      const centered = ((timeData[i] ?? 128) - 128) / 128
      peak = Math.max(peak, Math.abs(centered))
      sumSq += centered * centered
    }
    rms = Math.sqrt(sumSq / timeData.length)
  }

  let freqPeak = 0
  const usableBins = Math.max(1, Math.floor(frequencyData.length * 0.4))
  for (let i = 0; i < usableBins; i += 1) {
    freqPeak = Math.max(freqPeak, frequencyData[i] ?? 0)
  }
  const instant = Math.max(peak, rms * 1.4, freqPeak / 255)

  if (paused) {
    displayMaster *= 0.85
  } else {
    // Learn the quiet baseline quickly at first, then slowly.
    if (instant < adaptiveNoiseFloor) {
      adaptiveNoiseFloor = adaptiveNoiseFloor * 0.8 + instant * 0.2
    } else {
      adaptiveNoiseFloor = adaptiveNoiseFloor * 0.995 + instant * 0.005
    }
    adaptiveNoiseFloor = Math.min(0.18, Math.max(0.02, adaptiveNoiseFloor))

    // Only count energy clearly above the learned room floor.
    const gate = Math.max(0.08, adaptiveNoiseFloor * 3.5)
    const above =
      instant <= gate ? 0 : (instant - gate) / Math.max(0.12, 1 - gate)
    const target = Math.min(1, Math.pow(above * 5.5, 0.7))

    if (target > displayMaster) {
      displayMaster = displayMaster * 0.25 + target * 0.75
    } else {
      displayMaster *= 0.72
    }
    if (displayMaster < 0.03) {
      displayMaster = 0
    }
  }

  const master = displayMaster
  const silent = master < 0.05

  ctx.beginPath()
  ctx.arc(cx, cy, innerRadius, 0, Math.PI * 2)
  ctx.strokeStyle = "rgba(255, 172, 98, 0.4)"
  ctx.lineWidth = 2
  ctx.stroke()

  const binStep = Math.max(1, Math.floor(usableBins / barCount))
  for (let i = 0; i < barCount; i += 1) {
    let sum = 0
    const start = i * binStep
    for (let j = 0; j < binStep && start + j < usableBins; j += 1) {
      sum += frequencyData[start + j] ?? 0
    }
    const binRaw = sum / (binStep * 255)
    const gate = Math.max(0.08, adaptiveNoiseFloor * 3.5)
    const binAbove =
      binRaw <= gate ? 0 : (binRaw - gate) / Math.max(0.12, 1 - gate)
    const binLevel = silent ? 0 : Math.min(1, Math.pow(binAbove * 5.5, 0.7))
    const mixed = silent
      ? 0
      : Math.min(1, master * 0.9 + binLevel * 0.35 * master)
    const barLen = silent ? 1.25 : Math.max(2.5, mixed * maxBar)
    const angle = (i / barCount) * Math.PI * 2 - Math.PI / 2
    const x0 = cx + Math.cos(angle) * innerRadius
    const y0 = cy + Math.sin(angle) * innerRadius
    const x1 = cx + Math.cos(angle) * (innerRadius + barLen)
    const y1 = cy + Math.sin(angle) * (innerRadius + barLen)

    ctx.beginPath()
    ctx.moveTo(x0, y0)
    ctx.lineTo(x1, y1)
    ctx.strokeStyle = silent
      ? "rgba(154, 75, 18, 0.28)"
      : mixed > 0.2
      ? "#FFAC62"
      : "rgba(154, 75, 18, 0.5)"
    ctx.lineWidth = Math.max(2.25, cssWidth / 34)
    ctx.lineCap = "round"
    ctx.stroke()
  }

  ctx.beginPath()
  ctx.arc(cx, cy, Math.max(3.5, innerRadius * 0.22), 0, Math.PI * 2)
  ctx.fillStyle = paused
    ? "rgba(255, 172, 98, 0.35)"
    : silent
    ? "rgba(255, 172, 98, 0.5)"
    : "#FFAC62"
  ctx.fill()
}
